import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { AllJobsState } from "../../model";
import { getToken } from "../../utils/tokenStorage";
import { url } from "../../utils/config";
import { delayMessage } from "../../utils/delayMessage";
import { serverMessage } from "../../utils/serverMessage";

const initialState: AllJobsState = {
  isLoading: false,
  allJobsInputs: {
    search: "",
    status: "",
    type: "",
  },
  allJobs: [],
  resetJobsArray: [],
  jobCount: 0,
  pendingRequestTimer: null,
  isShowSingleJob: false,
  currentPage: 1,
  pageSize: 10,
  totalPages: 0,
};

export const allJobsAction = createAsyncThunk(
  "addJob",
  async ({ currentPage, pageSize }: any, thunkAPI) => {
    try {
      const token = getToken();
      const { data } = await axios.get(
        `${url.baseUrl}/job?page=${currentPage}&size=${pageSize}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(data);

      return data;
    } catch (error: any) {
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const allJobsSlice = createSlice({
  name: "allJobs",
  initialState,
  reducers: {
    collectInputs: (state, { payload }) => {
      const { name, value } = payload;
      state.allJobsInputs = { ...state.allJobsInputs, [name]: value };
    },

    clearInputs: (state) => {
      state.allJobsInputs.search = "";
      state.allJobsInputs.status = "";
      state.allJobsInputs.type = "";
    },

    increasePage: (state) => {
      state.currentPage = state.currentPage + 1;
    },
    decreasePage: (state) => {
      state.currentPage = state.currentPage - 1;
    },
    jobsPerPage: (state, { payload }) => {
      state.pageSize = payload;
    },

    searchForJobs: (state, { payload }) => {
      const { search, status, type } = payload;
      let filteredJobs = state.resetJobsArray.slice();

      // Filter by input search
      filteredJobs = filteredJobs.filter((each: any) => {
        return (
          each.company.toLowerCase().includes(search.toLowerCase()) ||
          each.position.toLowerCase().includes(search.toLowerCase())
        );
      });

      // Filter by job status
      filteredJobs = filteredJobs.filter((each: any) => {
        if (status) {
          return each.jobStatus === status;
        }
        return each;
      });

      // // Filter by job type
      filteredJobs = filteredJobs.filter((each: any) => {
        if (type) {
          return each.jobType === type;
        }
        return each;
      });

      state.allJobs = filteredJobs;
    },

    showSingleJobDetails: (state) => {
      state.isShowSingleJob = true;
    },
    hideSingleJobDetails: (state) => {
      state.isShowSingleJob = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // All Jobs API call
      .addCase(allJobsAction.pending, (state) => {
        state.isLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(allJobsAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.allJobs = payload.data.jobList;
        state.resetJobsArray = payload.data.jobList;
        state.jobCount = payload.data.count;
        state.totalPages = payload.data.totalPages;

        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
        // state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      })
      .addCase(allJobsAction.rejected, (state, { payload }: any) => {
        state.isLoading = false;
        serverMessage(payload); // Server error
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
        // state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      });
  },
});

export default allJobsSlice.reducer;

export const {
  collectInputs,
  clearInputs,
  searchForJobs,
  showSingleJobDetails,
  hideSingleJobDetails,
  increasePage,
  decreasePage,
  jobsPerPage,
} = allJobsSlice.actions;
